import React, { useState, useEffect, useRef } from 'react';

// Define menu items as a JSON array
const menuItems = [
  {
    title: 'HOME',
    link: '/#', // Changed to a unique ID for demonstration
    type: 'link',
  },
  {
    title: 'OUR STUDIO',
    link: '#our-studio',
    type: 'link',
  },
  {
    title: 'CLASSES',
    type: 'dropdown',
    submenu: [
      { name: 'Kids', link: '#class-details?name=kids' },
      { name: 'Adult', link: '#class-details?name=adult' },
      { name: 'Fitness', link: '#class-details?name=fitness' },
      { name: 'Online', link: '#class-details?name=online' },
      { name: 'Private', link: '#class-details?name=private' },
    ],
  },
  {
    title: 'EVENTS',
    type: 'dropdown',
    submenu: [
      { name: 'Wedding', link: '#wedding' },
      { name: 'Birthday', link: '#birthday' },
      { name: 'Surprise', link: '#surprise' },
      { name: 'Private Party', link: '#private-party' },
      { name: 'Collage Event', link: '#collage-event' },
    ],
  },
  {
    title: 'CONTACT',
    link: '#contact',
    type: 'link',
  },
];

// Helper function to determine active link from hash
const getActiveLinkFromHash = () => {
  const hash = window.location.hash.split('?')[0]; // Get hash before any query parameters
  const fullHash = window.location.hash; // Get full hash including query parameters

  if (hash === '/#' || hash === '') {
    return { main: 'HOME', sub: null };
  } else if (hash === '#our-studio') {
    return { main: 'OUR STUDIO', sub: null };
  } else if (['#class-details'].includes(hash)) {
    // Check if the full hash matches any of the class submenu links
    const foundSubmenu = menuItems.find(item => item.title === 'CLASSES')?.submenu.find(sub => sub.link === fullHash);
    return { main: 'CLASSES', sub: foundSubmenu ? foundSubmenu.name : null };
  } else if (['#wedding', '#birthday', '#surprise', '#private-party', '#collage-event'].includes(hash)) {
    const foundSubmenu = menuItems.find(item => item.title === 'EVENTS')?.submenu.find(sub => sub.link === fullHash);
    return { main: 'EVENTS', sub: foundSubmenu ? foundSubmenu.name : null };
  } else if (hash === '#contact') {
    return { main: 'CONTACT', sub: null };
  }
  return { main: 'HOME', sub: null }; // Default to Home if no matching hash
};

function Header() {
  const [isSticky, setIsSticky] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(getActiveLinkFromHash()); // Initialize with hash
  const socialLayerRef = useRef(null);
  const headerRef = useRef(null); // Ref for the header element
  const [headerHeight, setHeaderHeight] = useState(0); // State to store header height

  useEffect(() => {
    const handleScroll = () => {
      if (socialLayerRef.current) {
        const socialLayerBottom = socialLayerRef.current.getBoundingClientRect().bottom;
        if (socialLayerBottom <= 0) {
          setIsSticky(true);
        } else {
          setIsSticky(false);
        }
      }
    };

    const updateHeaderHeight = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    };

    const handleHashChange = () => {
      setActiveMenu(getActiveLinkFromHash());
    };

    // Set initial header height and update on resize
    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight);
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('hashchange', handleHashChange); // Listen for hash changes

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateHeaderHeight);
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleMenuItemClick = (mainTitle, link, subTitle = null) => {
    setActiveMenu({ main: mainTitle, sub: subTitle });
    setIsMobileMenuOpen(false); // Close mobile menu on item click
    if (link) {
      window.location.hash = link.substring(1); // Set the hash without the '#'
    }
  };

  return (
    <>
      <style jsx>{`
        /* Social Layer Styles */
        .social-layer-container {
          // background-color: black;
          // background-color: #808080;
          background-color: #293039;
          color: white;
          padding-top: 1rem;
          padding-bottom: 1rem;
          padding-left: 12rem;
          padding-right: 12rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          position: relative;
          z-index: 1001;
        }

        @media (max-width: 768px) {
          .social-layer-container {
            display: none;
          }
        }

        .social-layer-text {
          font-size: 1rem;
           font-family: 'Arial Black', sans-serif;
          font-weight : lighter;
          letter-spacing: 0.1em;
        }

        .social-icons-wrapper {
          display: flex;
          gap: 1rem;
        }

        .social-icon-link {
          width: 2rem;
          height: 2rem;
          border-radius: 9999px;
          background-color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background-color 0.3s ease, transform 0.3s ease;
          text-decoration: none;
        }

        .social-icon-link:hover {
          background-color: #e0e0e0;
          transform: scale(1.05);
        }

        .social-icon-link .fa {
          color: black;
          font-size: 1.2rem;
        }

        /* Header Styles */
        .header-container {
          background-color: white;
          color: black;
          padding: 1.5rem 12rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
          transition: all 0.3s ease-in-out;
          position: relative;
          z-index: 1000;
        }

        .header-container.sticky {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          background-color: rgba(255, 255, 255, 0.7); /* More transparent for glass effect */
          backdrop-filter: blur(10px); /* Increased blur for stronger glass effect */
          -webkit-backdrop-filter: blur(10px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          padding: 1.5rem 12rem; /* Maintain original padding */
          box-sizing: border-box; /* Add this to include padding in the width calculation */
        }

        .header-logo {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        }

        .header-logo-main {
          font-family: 'Impact', 'Arial Black', sans-serif;
          font-size: 1.5rem;
          font-weight: 900;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          margin-bottom: 0.2rem;
          display: flex;
          align-items: center;
        }

        .header-logo-star {
          font-size: 1.2rem;
          margin-right: 0.1em;
          color: black;
        }

        .header-logo-sub {
          font-family: sans-serif;
          font-size: 0.7rem;
          font-weight: bold;
          letter-spacing: 0.15em;
          text-transform: uppercase;
          color: #555;
        }

        .header-nav {
          display: flex;
          gap: 2rem;
        }

        .header-nav-item {
          font-family: sans-serif;
          font-size: 0.9rem;
          font-weight: 900;
          text-transform: uppercase;
          color: black;
          text-decoration: none;
          position: relative;
          padding-bottom: 0.3rem;
          cursor: pointer;
          transition: color 0.3s ease;
        }

        .header-nav-item:hover {
          color: #555; /* Keep text color slightly lighter on hover */
        }

        /* Underline for active/hover state */
        .header-nav-item::after {
          content: '';
          position: absolute;
          left: 0;
          bottom: 0;
          width: 100%;
          height: 2px;
          background-color: black;
          transform: scaleX(0);
          transform-origin: bottom left;
          transition: transform 0.3s ease-out; /* Apply transition to transform */
        }

        .header-nav-item:hover::after,
        .header-nav-item.active::after {
          transform: scaleX(0.5); /* Half-width underline on hover and for active item */
        }

        .header-nav-item-dropdown {
          display: flex;
          align-items: center;
          position: relative;
        }

        .header-nav-item-dropdown .dropdown-arrow {
          margin-left: 0.3rem;
          font-size: 0.6rem;
          transform: translateY(1px);
        }

        .dropdown-menu {
          position: absolute;
          top: 100%;
          left: 0;
          background-color: white;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
          min-width: 200px;
          list-style: none;
          padding: 0.5rem 0;
          margin: 0;
          display: none;
          flex-direction: column;
          z-index: 1001;
        }

        .header-nav-item-dropdown:hover .dropdown-menu {
          display: flex;
        }

        .dropdown-menu li a { /* Target the anchor tag inside the list item */
          padding: 0.75rem 1.5rem;
          font-family: sans-serif;
          font-size: 1rem;
          font-weight: 900;
          color: black; /* Default text color */
          text-decoration: none; /* Remove underline from links */
          display: block; /* Make the whole area clickable */
          transition: background-color 0.2s ease, color 0.2s ease;
          text-transform: none;
        }

        .dropdown-menu li a:hover,
        .dropdown-menu li a.active {
          background-color: black; /* Background on hover */
          color: white; /* Text color on hover */
        }

        /* Mobile Hamburger Menu */
        .hamburger-menu {
          display: none;
          flex-direction: column;
          cursor: pointer;
          padding: 0.5rem;
        }

        .hamburger-menu span {
          height: 3px;
          width: 25px;
          background-color: black;
          margin-bottom: 4px;
          border-radius: 2px;
          transition: all 0.3s ease-in-out;
        }

        .hamburger-menu span:last-child {
          margin-bottom: 0;
        }

        .hamburger-menu.open span:nth-child(1) {
          transform: rotate(45deg) translate(5px, 5px);
        }
        .hamburger-menu.open span:nth-child(2) {
          opacity: 0;
        }
        .hamburger-menu.open span:nth-child(3) {
          transform: rotate(-45deg) translate(5px, -5px);
        }

        /* Close button for mobile menu */
        .close-button {
          display: none;
          position: fixed;
          top: 1.5rem;
          right: 2rem;
          font-size: 2rem;
          color: black;
          cursor: pointer;
          z-index: 1002;
          transition: transform 0.3s ease-in-out;
        }

        .close-button.open {
          display: block;
          transform: rotate(90deg);
        }

        .mobile-nav-overlay {
          display: none;
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.7);
          z-index: 999;
          transition: opacity 0.3s ease-in-out;
          opacity: 0;
          pointer-events: none;
        }

        .mobile-nav-overlay.open {
          opacity: 1;
          pointer-events: auto;
        }

        .mobile-nav {
          display: none;
          position: fixed;
          top: 0;
          right: -300px;
          width: 300px;
          height: 100%;
          background-color: white;
          box-shadow: -2px 0 10px rgba(0, 0, 0, 0.2);
          flex-direction: column;
          padding-top: 4rem;
          transition: right 0.3s ease-in-out;
          z-index: 1000;
        }

        .mobile-nav.open {
          right: 0;
        }

        .mobile-nav-item {
          padding: 1rem 2rem;
          font-family: sans-serif;
          font-size: 1rem;
          font-weight: 900;
          text-transform: uppercase;
          color: black;
          text-decoration: none;
          border-bottom: 1px solid #eee;
          transition: background-color 0.2s ease;
        }

        .mobile-nav-item:hover {
          background-color: #f5f5f5;
        }

        .mobile-nav-item.active {
          background-color: #f0f0f0; /* Slightly different background for active mobile item */
        }

        .mobile-dropdown-toggle {
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 100%;
          cursor: pointer;
        }

        .mobile-dropdown-menu {
          list-style: none;
          padding: 0;
          margin :0;
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.3s ease-out;
        }

        .mobile-dropdown-menu.open {
          max-height: 400px;
        }

        .mobile-dropdown-menu li a { /* Target the anchor tag inside the list item */
          padding: 0.75rem;
          padding-top : 20px;
          font-family: sans-serif;
          font-size: 0.9rem;
          font-weight: 600;
          color: #333; /* Default text color */
          text-decoration: none; /* Remove underline from links */
          display: block; /* Make the whole area clickable */
          border-bottom: 1px solid #eee;
          transition: background-color 0.2s ease, color 0.2s ease;
        }

        .mobile-dropdown-menu li a:hover,
        .mobile-dropdown-menu li a.active {
          background-color: black; /* Background on hover */
          color: white; /* Text color on hover */
        }

        /* Media Queries for Responsiveness */
        @media (max-width: 1200px) {
          .social-layer-container, .header-container {
            padding-left: 5rem;
            padding-right: 5rem;
          }
          .header-container.sticky {
            padding: 1.5rem 5rem; /* Maintain original padding */
          }
        }

        @media (max-width: 992px) {
          .header-nav {
            display: none;
          }
          .hamburger-menu {
            display: flex;
          }
          .mobile-nav-overlay.open,
          .mobile-nav.open {
            display: flex;
          }
          .hamburger-menu.open {
            display: none;
          }
        }

        @media (max-width: 768px) {
          .social-layer-container {
            display: none;
          }
          .header-container {
            padding: 1rem 2rem;
          }
          .header-container.sticky {
            padding: 1rem 2rem; /* Maintain original padding */
          }
          .header-logo-main {
            font-size: 1.2rem;
          }
          .header-logo-sub {
            font-size: 0.6rem;
          }
          .mobile-nav {
            width: 250px;
          }
          .close-button {
            top: 1rem;
            right: 1.5rem;
          }
        }
      `}</style>

      {/* Social Layer */}
      <div className="social-layer-container" ref={socialLayerRef}>
        <div className="social-layer-text">
          JOIN OUR COMMUNITY OF OVER 200,000+ DREAMERS & DOERS
        </div>
        <div className="social-icons-wrapper">
          <a href="#" className="social-icon-link">
            <i className="fa fa-facebook"></i>
          </a>
          <a href="#" className="social-icon-link">
            <i className="fa fa-youtube"></i>
          </a>
          <a href="#" className="social-icon-link">
            <i className="fa fa-instagram"></i>
          </a>
          <a href="#" className="social-icon-link">
            <i className="fa fa-twitter"></i>
          </a>
        </div>
      </div>

      {/* Placeholder for sticky header */}
      {isSticky && <div style={{ height: `${headerHeight}px` }}></div>}

      {/* Header */}
      <header ref={headerRef} className={`header-container ${isSticky ? 'sticky' : ''}`}>
        <div className="header-logo">
          <div className="header-logo-main">
            <span className="header-logo-star">&#9733;</span>ABCD<span className="header-logo-star">&#9733;</span>
          </div>
          <div className="header-logo-sub">
            STUDIOS SALEM
          </div>
        </div>

        {/* Desktop Navigation */}
        <nav className="header-nav">
          {menuItems.map((item, index) => (
            item.type === 'link' ? (
              <a
                key={index}
                href={item.link}
                className={`header-nav-item ${activeMenu.main === item.title ? 'active' : ''}`}
                onClick={() => handleMenuItemClick(item.title, item.link)}
              >
                {item.title}
              </a>
            ) : (
              <div
                key={index}
                className={`header-nav-item header-nav-item-dropdown ${activeMenu.main === item.title ? 'active' : ''}`}
              >
                {item.title} <span className="dropdown-arrow">&#9660;</span>
                <ul className="dropdown-menu">
                  {item.submenu.map((subItem, subIndex) => (
                    <li key={subIndex}>
                      <a
                        href={subItem.link}
                        className={`${activeMenu.sub === subItem.name ? 'active' : ''}`}
                        onClick={() => handleMenuItemClick(item.title, subItem.link, subItem.name)} // Set parent dropdown and sub-item as active
                      >
                        {subItem.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )
          ))}
        </nav>

        {/* Hamburger Menu for Mobile (hidden when mobile menu is open) */}
        {!isMobileMenuOpen && (
          <div className="hamburger-menu" onClick={toggleMobileMenu}>
            <span></span>
            <span></span>
            <span></span>
          </div>
        )}
      </header>

      {/* Mobile Navigation Overlay */}
      <div className={`mobile-nav-overlay ${isMobileMenuOpen ? 'open' : ''}`} onClick={toggleMobileMenu}></div>

      {/* Mobile Navigation */}
      <nav className={`mobile-nav ${isMobileMenuOpen ? 'open' : ''}`}>
        {/* Close button for mobile menu */}
        <div className={`close-button ${isMobileMenuOpen ? 'open' : ''}`} onClick={toggleMobileMenu}>
          &times;
        </div>
        {menuItems.map((item, index) => (
          item.type === 'link' ? (
            <a
              key={index}
              href={item.link}
              className={`mobile-nav-item ${activeMenu.main === item.title ? 'active' : ''}`}
              onClick={() => handleMenuItemClick(item.title, item.link)}
            >
              {item.title}
            </a>
          ) : (
            <MobileDropdownItem
              key={index}
              title={item.title}
              toggleMobileMenu={toggleMobileMenu}
              setActiveMenu={setActiveMenu} // Pass setActiveMenu directly
              activeMenu={activeMenu} // Pass activeMenu directly
            >
              {item.submenu.map((subItem, subIndex) => (
                <li key={subIndex}>
                  <a
                    href={subItem.link}
                    className={`${activeMenu.sub === subItem.name ? 'active' : ''}`}
                    onClick={() => handleMenuItemClick(item.title, subItem.link, subItem.name)} // Set parent dropdown and sub-item as active
                  >
                    {subItem.name}
                  </a>
                </li>
              ))}
            </MobileDropdownItem>
          )
        ))}
      </nav>
    </>
  );
}

// Helper component for mobile dropdowns
function MobileDropdownItem({ title, children, toggleMobileMenu, setActiveMenu, activeMenu }) {
  const [isOpen, setIsOpen] = useState(false);

  // Open the dropdown if its parent is the active main menu item and a submenu item is active
  useEffect(() => {
    if (activeMenu.main === title && activeMenu.sub) {
      setIsOpen(true);
    } else {
      setIsOpen(false); // Close if not active
    }
  }, [activeMenu, title]);

  const handleToggleAndClose = (e) => {
    if (e.target.tagName !== 'A') {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div className={`mobile-nav-item ${activeMenu.main === title ? 'active' : ''}`}>
      <div className="mobile-dropdown-toggle" onClick={handleToggleAndClose}>
        {title}
        <span className="dropdown-arrow">{isOpen ? '▲' : '▼'}</span>
      </div>
      <ul className={`mobile-dropdown-menu ${isOpen ? 'open' : ''}`}>
        {React.Children.map(children, child =>
          React.cloneElement(child, { onClick: child.props.onClick }) // Keep original onClick from parent
        )}
      </ul>
    </div>
  );
}

export default Header;